// A minimal QR encoder: byte mode, error-correction level M, versions 1 to 10 (up to 213
// bytes), which covers otpauth URIs. Written against ISO/IEC 18004; mask pattern 0 only, which
// is always valid even if not always the prettiest.


const EC_CODEWORDS_M = [0, 10, 16, 26, 36, 48, 64, 72, 88, 110, 130]
const EC_BLOCKS_M = [0, 1, 1, 1, 2, 2, 4, 4, 4, 5, 5]
const TOTAL_CODEWORDS = [0, 26, 44, 70, 100, 134, 172, 196, 242, 292, 346]
const ALIGN_POS: number[][] = [[], [], [6, 18], [6, 22], [6, 26], [6, 30], [6, 34], [6, 22, 38], [6, 24, 42], [6, 26, 46], [6, 28, 50]]

export function encodeQr(text: string): boolean[][] | null {
  const data = new TextEncoder().encode(text)
  let version = 0
  for (let v = 1; v <= 10; v++) {
    const capacity = TOTAL_CODEWORDS[v]! - EC_CODEWORDS_M[v]!
    if (data.length + 2 + (v >= 10 ? 1 : 0) <= capacity) {
      version = v
      break
    }
  }
  if (!version) return null
  const totalData = TOTAL_CODEWORDS[version]! - EC_CODEWORDS_M[version]!
  // Bit stream: mode 0100, length (8 bits for v1-9, 16 for v10), bytes, terminator, pad.
  const bits: number[] = []
  const push = (val: number, len: number) => {
    for (let i = len - 1; i >= 0; i--) bits.push((val >> i) & 1)
  }
  push(0b0100, 4)
  push(data.length, version >= 10 ? 16 : 8)
  for (const b of data) push(b, 8)
  push(0, Math.min(4, totalData * 8 - bits.length))
  while (bits.length % 8) bits.push(0)
  const codewords: number[] = []
  for (let i = 0; i < bits.length; i += 8) codewords.push(parseInt(bits.slice(i, i + 8).join(''), 2))
  for (let pad = 0xec; codewords.length < totalData; pad = pad === 0xec ? 0x11 : 0xec) codewords.push(pad)

  // Reed–Solomon over GF(256), split into blocks.
  const blocks = EC_BLOCKS_M[version]!
  const ecPerBlock = EC_CODEWORDS_M[version]! / blocks
  const shortLen = Math.floor(totalData / blocks)
  const longBlocks = totalData % blocks
  const dataBlocks: number[][] = []
  let offset = 0
  for (let b = 0; b < blocks; b++) {
    const len = shortLen + (b >= blocks - longBlocks ? 1 : 0)
    dataBlocks.push(codewords.slice(offset, offset + len))
    offset += len
  }
  const ecBlocks = dataBlocks.map((d) => rsEncode(d, ecPerBlock))
  const interleaved: number[] = []
  const maxLen = Math.max(...dataBlocks.map((d) => d.length))
  for (let i = 0; i < maxLen; i++) for (const d of dataBlocks) if (i < d.length) interleaved.push(d[i]!)
  for (let i = 0; i < ecPerBlock; i++) for (const e of ecBlocks) interleaved.push(e[i]!)

  const n = version * 4 + 17
  const grid: (boolean | null)[][] = Array.from({ length: n }, () => Array<boolean | null>(n).fill(null))
  const set = (x: number, y: number, v: boolean) => {
    grid[y]![x] = v
  }
  const finder = (cx: number, cy: number) => {
    for (let dy = -1; dy <= 7; dy++)
      for (let dx = -1; dx <= 7; dx++) {
        const x = cx + dx
        const y = cy + dy
        if (x < 0 || y < 0 || x >= n || y >= n) continue
        const on = dx >= 0 && dx <= 6 && dy >= 0 && dy <= 6 && (dx === 0 || dx === 6 || dy === 0 || dy === 6 || (dx >= 2 && dx <= 4 && dy >= 2 && dy <= 4))
        set(x, y, on)
      }
  }
  finder(0, 0)
  finder(n - 7, 0)
  finder(0, n - 7)
  for (let i = 8; i < n - 8; i++) {
    set(i, 6, i % 2 === 0)
    set(6, i, i % 2 === 0)
  }
  const aligns = ALIGN_POS[version]!
  const last = aligns.length - 1
  for (let i = 0; i < aligns.length; i++)
    for (let j = 0; j < aligns.length; j++) {
      if ((i === 0 && j === 0) || (i === 0 && j === last) || (i === last && j === 0)) continue // under a finder
      const ax = aligns[j]!
      const ay = aligns[i]!
      for (let dy = -2; dy <= 2; dy++) for (let dx = -2; dx <= 2; dx++) set(ax + dx, ay + dy, Math.max(Math.abs(dx), Math.abs(dy)) !== 1)
    }
  // Reserve format areas (filled after masking).
  for (let i = 0; i < 9; i++) {
    if (grid[8]![i] === null) set(i, 8, false)
    if (grid[i]![8] === null) set(8, i, false)
  }
  for (let i = 0; i < 8; i++) {
    set(n - 1 - i, 8, false)
    set(8, n - 1 - i, false)
  }
  set(8, n - 8, true)
  // Version information (two 6×3 blocks) exists from version 7; it must be in place before
  // the data is laid out around it.
  if (version >= 7) {
    const vbits = VERSION_BITS[version]!
    for (let i = 0; i < 18; i++) {
      const bit = ((vbits >> i) & 1) === 1
      set(Math.floor(i / 3), n - 11 + (i % 3), bit)
      set(n - 11 + (i % 3), Math.floor(i / 3), bit)
    }
  }

  // Place data bits in the zigzag, applying mask 0 ((x+y) % 2 == 0).
  const dataBits: number[] = []
  for (const cw of interleaved) for (let i = 7; i >= 0; i--) dataBits.push((cw >> i) & 1)
  let bi = 0
  const isData: boolean[][] = Array.from({ length: n }, () => Array<boolean>(n).fill(false))
  for (let right = n - 1; right >= 1; right -= 2) {
    if (right === 6) right = 5
    for (let vert = 0; vert < n; vert++) {
      for (let j = 0; j < 2; j++) {
        const x = right - j
        const upward = ((right + 1) & 2) === 0
        const y = upward ? n - 1 - vert : vert
        if (grid[y]![x] !== null) continue
        const bit = bi < dataBits.length ? dataBits[bi]! : 0
        bi++
        set(x, y, (bit === 1) !== ((x + y) % 2 === 0))
        isData[y]![x] = true
      }
    }
  }
  // Format bits for EC level M and mask 0 (BCH-coded and XOR-masked per the standard).
  const format = 0b101010000010010
  const fbit = (i: number) => ((format >> i) & 1) === 1
  for (let i = 0; i <= 5; i++) set(8, i, fbit(i))
  set(8, 7, fbit(6))
  set(8, 8, fbit(7))
  set(7, 8, fbit(8))
  for (let i = 9; i < 15; i++) set(14 - i, 8, fbit(i))
  for (let i = 0; i < 8; i++) set(n - 1 - i, 8, fbit(i))
  for (let i = 8; i < 15; i++) set(8, n - 15 + i, fbit(i))
  set(8, n - 8, true)
  return grid.map((row) => row.map((v) => v === true))
}

const VERSION_BITS: Record<number, number> = { 7: 0x07c94, 8: 0x085bc, 9: 0x09a99, 10: 0x0a4d3 }

const GF_EXP = new Array<number>(512)
const GF_LOG = new Array<number>(256)
;(() => {
  let x = 1
  for (let i = 0; i < 255; i++) {
    GF_EXP[i] = x
    GF_LOG[x] = i
    x <<= 1
    if (x & 0x100) x ^= 0x11d
  }
  for (let i = 255; i < 512; i++) GF_EXP[i] = GF_EXP[i - 255]!
})()

function gfMul(a: number, b: number): number {
  if (a === 0 || b === 0) return 0
  return GF_EXP[GF_LOG[a]! + GF_LOG[b]!]!
}

function rsEncode(data: number[], ecLen: number): number[] {
  let gen = [1]
  for (let i = 0; i < ecLen; i++) {
    const next = new Array<number>(gen.length + 1).fill(0)
    for (let j = 0; j < gen.length; j++) {
      next[j] = (next[j] ?? 0) ^ gen[j]!
      next[j + 1] = (next[j + 1] ?? 0) ^ gfMul(gen[j]!, GF_EXP[i]!)
    }
    gen = next
  }
  const out = new Array<number>(ecLen).fill(0)
  for (const d of data) {
    const factor = d ^ out[0]!
    out.shift()
    out.push(0)
    if (factor !== 0) for (let j = 0; j < ecLen; j++) out[j] = (out[j] ?? 0) ^ gfMul(gen[j + 1]!, factor)
  }
  return out
}
